import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Chip,
  Button,
  Card,
  CardContent,
  GridLegacy as Grid,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  styled,
  alpha,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Send,
  Psychology,
  AutoAwesome,
  Star,
  MoreVert,
  Bookmark,
  Share,
  ThumbUp,
  ThumbDown,
  Refresh,
  Favorite,
  TrendingUp,
  Schedule,
  Mic,
  Stop,
  Check
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Types
interface ChatMessage {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
  isTyping?: boolean;
  metadata?: {
    category?: string;
    confidence?: number;
    sources?: string[];
    suggestions?: string[];
  };
}

interface QuickAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  category: string;
  prompt: string;
}

interface ChatSession {
  id: string;
  title: string;
  timestamp: Date;
  messageCount: number;
  category: string;
}

// Custom theme colors (using your brand palette)
const brandColors = {
  primary: {
    main: '#3B82F6',
    light: '#60A5FA',
    dark: '#1E40AF',
  },
  secondary: {
    main: '#8B5CF6',
    light: '#A78BFA',
    dark: '#6D28D9',
  },
  accent: {
    main: '#F97316',
    light: '#FB923C',
    dark: '#EA580C',
  }
};

// Styled Components
const ChatContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: `linear-gradient(135deg, 
    #030712 0%, 
    ${alpha(brandColors.primary.dark, 0.1)} 25%,
    ${alpha(brandColors.secondary.dark, 0.05)} 75%,
    #030712 100%)`,
  position: 'relative',
  overflow: 'hidden',
}));

const ChatPanel = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha('#1E293B', 0.8)} 0%, 
    ${alpha(brandColors.primary.main, 0.05)} 100%)`,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
  borderRadius: '24px',
  height: '80vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const MessageBubble = styled(Box)<{ isUser?: boolean }>(({ theme, isUser }) => ({
  background: isUser 
    ? `linear-gradient(135deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`
    : `linear-gradient(135deg, ${alpha('#1E293B', 0.8)}, ${alpha('#334155', 0.6)})`,
  color: 'white',
  padding: theme.spacing(2, 3),
  borderRadius: isUser ? '20px 20px 8px 20px' : '20px 20px 20px 8px',
  maxWidth: '70%',
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  margin: theme.spacing(1, 0),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(isUser ? brandColors.primary.main : '#475569', 0.3)}`,
  position: 'relative',
  animation: 'slideIn 0.3s ease-out',
  '@keyframes slideIn': {
    from: {
      opacity: 0,
      transform: isUser ? 'translateX(20px)' : 'translateX(-20px)',
    },
    to: {
      opacity: 1,
      transform: 'translateX(0)',
    },
  },
}));

const QuickActionCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha('#1E293B', 0.6)} 0%, 
    ${alpha(brandColors.primary.main, 0.1)} 100%)`,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
  borderRadius: '16px',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: brandColors.primary.main,
    boxShadow: `0 10px 30px ${alpha(brandColors.primary.main, 0.3)}`,
  },
}));

const SidebarCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha('#1E293B', 0.6)} 0%, 
    ${alpha(brandColors.secondary.main, 0.1)} 100%)`,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha(brandColors.secondary.main, 0.2)}`,
  borderRadius: '20px',
  marginBottom: theme.spacing(3),
}));

const TypingIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2, 3),
  background: `linear-gradient(135deg, ${alpha('#1E293B', 0.8)}, ${alpha('#334155', 0.6)})`,
  borderRadius: '20px 20px 20px 8px',
  maxWidth: '70%',
  alignSelf: 'flex-start',
  margin: theme.spacing(1, 0),
  '& .dot': {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: brandColors.primary.light,
    animation: 'typing 1.4s infinite ease-in-out',
    '&:nth-of-type(1)': { animationDelay: '0s' },
    '&:nth-of-type(2)': { animationDelay: '0.2s' },
    '&:nth-of-type(3)': { animationDelay: '0.4s' },
  },
  '@keyframes typing': {
    '0%, 60%, 100%': {
      transform: 'translateY(0)',
      opacity: 0.4,
    },
    '30%': {
      transform: 'translateY(-10px)',
      opacity: 1,
    },
  },
}));

const Chat: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState<string>('');
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [selectedSession, setSelectedSession] = useState<string>('current');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState<boolean>(false);
  const [isRecording, setIsRecording] = useState<boolean>(false);

  // Mock user data (in real app, this would come from your user context)
  const userProfile = {
    name: 'Sarah',
    birthSign: 'Gemini',
    moonSign: 'Scorpio',
    risingSign: 'Leo',
    interests: ['Love & Relationships', 'Career Guidance', 'Personal Growth']
  };

  // Quick action suggestions based on user interests
  const quickActions: QuickAction[] = [
    {
      id: '1',
      label: t('chat.quickActions.todaysLoveForecast'),
      icon: <Favorite sx={{ color: '#EC4899' }} />,
      category: 'love',
      prompt: t('chat.quickActions.prompts.loveForecast')
    },
    {
      id: '2',
      label: t('chat.quickActions.careerGuidance'),
      icon: <TrendingUp sx={{ color: '#10B981' }} />,
      category: 'career',
      prompt: t('chat.quickActions.prompts.careerGuidance')
    },
    {
      id: '3',
      label: t('chat.quickActions.personalGrowth'),
      icon: <Psychology sx={{ color: brandColors.secondary.main }} />,
      category: 'growth',
      prompt: t('chat.quickActions.prompts.personalGrowth')
    },
    {
      id: '4',
      label: t('chat.quickActions.mercuryRetrograde'),
      icon: <AutoAwesome sx={{ color: brandColors.accent.main }} />,
      category: 'transits',
      prompt: t('chat.quickActions.prompts.mercuryRetrograde')
    },
    {
      id: '5',
      label: t('chat.quickActions.luckyTiming'),
      icon: <Schedule sx={{ color: '#FBBF24' }} />,
      category: 'timing',
      prompt: t('chat.quickActions.prompts.luckyTiming')
    },
    {
      id: '6',
      label: t('chat.quickActions.moonPhaseImpact'),
      icon: <Star sx={{ color: '#A855F7' }} />,
      category: 'lunar',
      prompt: t('chat.quickActions.prompts.moonPhaseImpact')
    }
  ];

  // Recent chat sessions (mock data)
  const chatSessions: ChatSession[] = [
    {
      id: 'current',
      title: t('chat.sessions.currentSession'),
      timestamp: new Date(),
      messageCount: messages.length,
      category: 'general'
    },
    {
      id: '1',
      title: t('chat.sessions.careerPathInsights'),
      timestamp: new Date(Date.now() - 86400000),
      messageCount: 12,
      category: 'career'
    },
    {
      id: '2',
      title: t('chat.sessions.loveCompatibility'),
      timestamp: new Date(Date.now() - 172800000),
      messageCount: 8,
      category: 'love'
    },
    {
      id: '3',
      title: t('chat.sessions.mercuryRetrogradePrep'),
      timestamp: new Date(Date.now() - 259200000),
      messageCount: 15,
      category: 'transits'
    }
  ];

  // Initialize with welcome message
  useEffect(() => {
    if (messages.length === 0) {
      const welcomeMessage: ChatMessage = {
        id: 'welcome',
        type: 'ai',
        content: t(
          'chat.welcomeMessage',
          { 
            name: userProfile.name, 
            birthSign: userProfile.birthSign, 
            moonSign: userProfile.moonSign, 
            risingSign: userProfile.risingSign 
          }
        ),
        timestamp: new Date(),
        metadata: {
          category: 'welcome',
          confidence: 1.0,
          suggestions: [
            t('chat.suggestions.tellMeForecast'),
            t('chat.suggestions.focusThisWeek'),
            t('chat.suggestions.useStrengths')
          ]
        }
      };
      setMessages([welcomeMessage]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const [userPlan] = useState<string>('pro'); // TODO: setUserPlan get user plan from backend
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!inputText.trim()) return;

    // Check plan limits for free users
    if (userPlan !== 'pro' && messages.filter(m => m.type === 'user').length >= 3) {
    // if (userPlan === 'free' && messages.filter(m => m.type === 'user').length >= 3) {
      setUpgradeDialogOpen(true);
      return;
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsTyping(true);

    // Simulate AI response (in real app, this would be an API call)
    setTimeout(() => {
      const aiResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: generateAIResponse(inputText),
        timestamp: new Date(),
        metadata: {
          category: 'general',
          confidence: 0.85,
          suggestions: [
            'Tell me more about this',
            'What else should I know?',
            'How can I apply this guidance?'
          ]
        }
      };
      setMessages(prev => [...prev, aiResponse]);
      setIsTyping(false);
    }, 2000);
  };

  const generateAIResponse = (input: string): string => {
    // Simple response generator (in real app, this would call your AI API)
    const responses = [
      `Based on your ${userProfile.birthSign} nature and current planetary transits, I see opportunities for growth in this area. Your ${userProfile.moonSign} moon adds emotional depth to this situation.`,
      `The cosmic energies are aligning favorably for you. As a ${userProfile.birthSign}, you have natural strengths that can help you navigate this. Your rising sign ${userProfile.risingSign} suggests approaching this with confidence.`,
      `Your birth chart reveals interesting insights about this topic. The current Mercury position encourages clear communication, while your ${userProfile.moonSign} moon urges you to trust your intuition.`,
      `This is a powerful time for ${userProfile.birthSign} individuals like yourself. The planetary aspects suggest focusing on your core values and long-term vision. Your ${userProfile.risingSign} rising gives you the charisma to manifest your goals.`
    ];
    return responses[Math.floor(Math.random() * responses.length)];
  };

  const handleQuickAction = (action: QuickAction) => {
    setInputText(action.prompt);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const renderMessage = (message: ChatMessage) => {
    if (message.type === 'ai') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Avatar sx={{ 
            background: `linear-gradient(135deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`,
            width: 40, 
            height: 40 
          }}>
            <Psychology />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <MessageBubble isUser={false}>
              <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                {message.content}
              </Typography>
              {message.metadata?.confidence && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    size="small" 
                    label={t(
                      'chat.confident', 
                      { percentage: Math.round(message.metadata.confidence * 100) }
                    )}
                    sx={{ 
                      background: alpha(brandColors.primary.main, 0.2),
                      color: brandColors.primary.light,
                      fontSize: '0.7rem'
                    }}
                  />
                </Box>
              )}
            </MessageBubble>
            
            {/* AI Message Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, pl: 1 }}>
              <Tooltip title={t('chat.helpful')}>
                <IconButton size="small" sx={{ color: '#64748B' }}>
                  <ThumbUp sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('chat.notHelpful')}>
                <IconButton size="small" sx={{ color: '#64748B' }}>
                  <ThumbDown sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('chat.bookmark')}>
                <IconButton size="small" sx={{ color: '#64748B' }}>
                  <Bookmark sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('chat.share')}>
                <IconButton size="small" sx={{ color: '#64748B' }}>
                  <Share sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Suggestions */}
            {message.metadata?.suggestions && (
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {message.metadata.suggestions.map((suggestion, index) => (
                  <Chip
                    key={index}
                    label={suggestion}
                    size="small"
                    onClick={() => setInputText(suggestion)}
                    sx={{
                      background: alpha(brandColors.secondary.main, 0.1),
                      color: brandColors.secondary.light,
                      border: `1px solid ${alpha(brandColors.secondary.main, 0.3)}`,
                      '&:hover': {
                        background: alpha(brandColors.secondary.main, 0.2),
                      },
                      cursor: 'pointer'
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      );
    } else {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <MessageBubble isUser={true}>
            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
              {message.content}
            </Typography>
          </MessageBubble>
        </Box>
      );
    }
  };

  return (
    <ChatContainer>
      {/* Animated Background */}
      <Box sx={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        {Array.from({ length: 20 }).map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `twinkle ${2 + Math.random() * 3}s infinite`,
              animationDelay: `${Math.random() * 3}s`,
              '@keyframes twinkle': {
                '0%, 100%': { opacity: 0.3 },
                '50%': { opacity: 0.8 },
              },
            }}
          >
            <Star sx={{ fontSize: 6, color: 'white' }} />
          </Box>
        ))}
      </Box>

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, py: 4 }}>
        <Grid container spacing={3} sx={{ height: '100%' }}>
          {/* Sidebar - Chat History & Quick Actions */}
          {!isMobile && (
            <Grid item md={3}>
              {/* Quick Actions */}
              <SidebarCard>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                    {t('chat.quickActions')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {quickActions.slice(0, 4).map((action) => (
                      <QuickActionCard key={action.id} onClick={() => handleQuickAction(action)}>
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {action.icon}
                            <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                              {action.label}
                            </Typography>
                          </Box>
                        </CardContent>
                      </QuickActionCard>
                    ))}
                  </Box>
                </CardContent>
              </SidebarCard>

              {/* Chat History */}
              <SidebarCard>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'white' }}>
                    {t('chat.recentChats')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {chatSessions.map((session) => (
                      <Box
                        key={session.id}
                        onClick={() => setSelectedSession(session.id)}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          cursor: 'pointer',
                          background: selectedSession === session.id 
                            ? alpha(brandColors.primary.main, 0.2)
                            : 'transparent',
                          border: `1px solid ${selectedSession === session.id 
                            ? brandColors.primary.main 
                            : 'transparent'}`,
                          '&:hover': {
                            background: alpha(brandColors.primary.main, 0.1),
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 500, mb: 0.5 }}>
                          {session.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                          {t('chat.sessions.messages', { count: session.messageCount })} • {session.timestamp.toLocaleDateString()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </SidebarCard>
            </Grid>
          )}

          {/* Main Chat Panel */}
          <Grid item xs={12} md={isMobile ? 12 : 9}>
            <ChatPanel>
              {/* Chat Header */}
              <Box sx={{ 
                p: 3, 
                borderBottom: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
                background: alpha('#1E293B', 0.3),
                borderRadius: '24px 24px 0 0'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ 
                      background: `linear-gradient(135deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`,
                      width: 48, 
                      height: 48 
                    }}>
                      <Psychology />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                        {t('chat.aiAstrologer')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                        {t('chat.personalCosmicGuide')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {userPlan !== 'elite' && (
                      <Chip
                        label={userPlan === 'free' 
                          ? t('chat.freeLeft', { count: 3 - messages.filter(m => m.type === 'user').length })
                          : t('chat.proPlan')
                        }
                        size="small"
                        sx={{
                          background: userPlan === 'free' 
                            ? alpha('#EF4444', 0.2) 
                            : alpha(brandColors.primary.main, 0.2),
                          color: userPlan === 'free' ? '#EF4444' : brandColors.primary.light,
                          fontWeight: 600
                        }}
                      />
                    )}
                    <IconButton 
                      sx={{ color: '#94A3B8' }}
                      onClick={(e) => setMenuAnchor(e.currentTarget)}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>
                </Box>
              </Box>

              {/* Messages Area */}
              <Box sx={{ 
                flex: 1, 
                overflow: 'auto', 
                p: 3,
                display: 'flex',
                flexDirection: 'column'
              }}>
                {messages.map((message) => renderMessage(message))}
                
                {/* Typing Indicator */}
                {isTyping && (
                  <TypingIndicator>
                    <Avatar sx={{ 
                      width: 24, 
                      height: 24,
                      background: `linear-gradient(135deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`
                    }}>
                      <Psychology sx={{ fontSize: 14 }} />
                    </Avatar>
                    <Typography variant="body2" sx={{ color: '#94A3B8', mr: 2 }}>
                      {t('chat.aiThinking')}
                    </Typography>
                    <Box className="dot" />
                    <Box className="dot" />
                    <Box className="dot" />
                  </TypingIndicator>
                )}
                
                <div ref={messagesEndRef} />
              </Box>

              {/* Quick Actions Bar (Mobile) */}
              {isMobile && (
                <Box sx={{ p: 2, borderTop: `1px solid ${alpha(brandColors.primary.main, 0.2)}` }}>
                  <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                    {quickActions.slice(0, 3).map((action) => (
                      <Chip
                        key={action.id}
                        label={action.label}
                        size="small"
                        onClick={() => handleQuickAction(action)}
                        sx={{
                          background: alpha(brandColors.primary.main, 0.1),
                          color: brandColors.primary.light,
                          border: `1px solid ${alpha(brandColors.primary.main, 0.3)}`,
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            background: alpha(brandColors.primary.main, 0.2),
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Input Area */}
              <Box sx={{ 
                p: 3, 
                borderTop: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
                background: alpha('#1E293B', 0.3)
              }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={t('chat.askPlaceholder')}
                    disabled={userPlan === 'free' && messages.filter(m => m.type === 'user').length >= 3}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: alpha('#0F172A', 0.6),
                        borderRadius: '20px',
                        '& fieldset': { 
                          borderColor: alpha(brandColors.primary.main, 0.3),
                          borderWidth: 1
                        },
                        '&:hover fieldset': { borderColor: brandColors.primary.main },
                        '&.Mui-focused fieldset': { borderColor: brandColors.primary.light },
                      },
                      '& .MuiInputBase-input': { 
                        color: 'white',
                        '&::placeholder': { color: '#94A3B8' }
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton 
                      sx={{ 
                        color: '#94A3B8',
                        '&:hover': { color: brandColors.primary.light }
                      }}
                      onClick={() => setIsRecording(!isRecording)}
                    >
                      {isRecording ? <Stop /> : <Mic />}
                    </IconButton>
                    <IconButton 
                      onClick={handleSendMessage}
                      disabled={!inputText.trim() || (userPlan === 'free' && messages.filter(m => m.type === 'user').length >= 3)}
                      sx={{
                        background: inputText.trim() 
                          ? `linear-gradient(135deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`
                          : alpha('#64748B', 0.3),
                        color: 'white',
                        '&:hover': {
                          background: inputText.trim() 
                            ? `linear-gradient(135deg, ${brandColors.primary.light}, ${brandColors.secondary.light})`
                            : alpha('#64748B', 0.3),
                        },
                        '&:disabled': {
                          background: alpha('#64748B', 0.3),
                          color: '#94A3B8',
                        }
                      }}
                    >
                      <Send />
                    </IconButton>
                  </Box>
                </Box>
                
                {userPlan === 'free' && messages.filter(m => m.type === 'user').length >= 2 && (
                  <Typography variant="caption" sx={{ color: '#EF4444', mt: 1, display: 'block' }}>
                    {t('chat.freeQuestionsRemaining', { count: 3 - messages.filter(m => m.type === 'user').length })}
                  </Typography>
                )}
              </Box>
            </ChatPanel>
          </Grid>
        </Grid>
      </Container>

      {/* Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${alpha('#1E293B', 0.95)}, ${alpha(brandColors.primary.main, 0.1)})`,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
            borderRadius: '12px',
            color: 'white'
          }
        }}
      >
        <MenuItem onClick={() => setMenuAnchor(null)}>
          <Refresh sx={{ mr: 2 }} />
          {t('chat.newChat')}
        </MenuItem>
        <MenuItem onClick={() => setMenuAnchor(null)}>
          <Bookmark sx={{ mr: 2 }} />
          {t('chat.saveConversation')}
        </MenuItem>
        <MenuItem onClick={() => setMenuAnchor(null)}>
          <Share sx={{ mr: 2 }} />
          {t('chat.shareChat')}
        </MenuItem>
        <Divider sx={{ borderColor: alpha(brandColors.primary.main, 0.2) }} />
        <MenuItem onClick={() => setUpgradeDialogOpen(true)}>
          <AutoAwesome sx={{ mr: 2, color: brandColors.accent.main }} />
          {t('chat.upgradePlan')}
        </MenuItem>
      </Menu>

      {/* Upgrade Dialog */}
      <Dialog
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${alpha('#1E293B', 0.95)}, ${alpha(brandColors.primary.main, 0.1)})`,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(brandColors.primary.main, 0.2)}`,
            borderRadius: '20px',
            color: 'white'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <AutoAwesome sx={{ fontSize: 48, color: brandColors.accent.main, mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {t('chat.unlockUnlimitedGuidance')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#94A3B8' }}>
            {t('chat.continueCosmicJourney')}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={3}>
            {/* Pro Plan */}
            <Grid item xs={12} sm={6}>
              <Card sx={{
                background: `linear-gradient(135deg, ${alpha(brandColors.primary.main, 0.2)}, ${alpha(brandColors.secondary.main, 0.1)})`,
                border: `2px solid ${brandColors.primary.main}`,
                borderRadius: '16px',
                position: 'relative'
              }}>
                <Chip 
                  label={t('chat.mostPopular')}
                  sx={{ 
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: `linear-gradient(135deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`,
                    color: 'white',
                    fontWeight: 600
                  }} 
                />
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    {t('chat.pro')}
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    $4.99
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 3 }}>
                    {t('chat.perMonth')}
                  </Typography>
                  <Box sx={{ textAlign: 'left', mb: 3, minHeight: 120 }}>
                    <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 1, display: 'flex', alignItems: 'center' }}>
                      <Check sx={{ mr: 1, fontSize: 16, color: '#10B981' }} />
                      {t('chat.unlimitedConversations')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 1, display: 'flex', alignItems: 'center' }}>
                      <Check sx={{ mr: 1, fontSize: 16, color: '#10B981' }} />
                      {t('chat.detailedBirthChart')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 1, display: 'flex', alignItems: 'center' }}>
                      <Check sx={{ mr: 1, fontSize: 16, color: '#10B981' }} />
                      {t('chat.personalizedInsights')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 1, display: 'flex', alignItems: 'center' }}>
                      <Check sx={{ mr: 1, fontSize: 16, color: '#10B981' }} />
                      {t('chat.transitNotifications')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Elite Plan */}
            <Grid item xs={12} sm={6}>
              <Card sx={{
                background: `linear-gradient(135deg, ${alpha('#1E293B', 0.8)}, ${alpha(brandColors.secondary.main, 0.1)})`,
                border: `1px solid ${alpha(brandColors.secondary.main, 0.3)}`,
                borderRadius: '16px'
              }}>
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    {t('chat.elite')}
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    $9.99
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 3 }}>
                    {t('chat.perMonth')}
                  </Typography>
                  <Box sx={{ textAlign: 'left', mb: 3, minHeight: 120 }}>
                    <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 1, display: 'flex', alignItems: 'center' }}>
                      <Check sx={{ mr: 1, fontSize: 16, color: '#10B981' }} />
                      {t('chat.everythingInPro')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 1, display: 'flex', alignItems: 'center' }}>
                      <Check sx={{ mr: 1, fontSize: 16, color: '#10B981' }} />
                      {t('chat.priorityResponses')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 1, display: 'flex', alignItems: 'center' }}>
                      <Check sx={{ mr: 1, fontSize: 16, color: '#10B981' }} />
                      {t('chat.advancedCompatibility')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#CBD5E1', mb: 1, display: 'flex', alignItems: 'center' }}>
                      <Check sx={{ mr: 1, fontSize: 16, color: '#10B981' }} />
                      {t('chat.exclusiveFeatures')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#94A3B8', mb: 2 }}>
              {t('chat.freeTrial')}
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setUpgradeDialogOpen(false)}
            sx={{ color: '#94A3B8' }}
          >
            {t('chat.maybeLater')}
          </Button>
          <Button
            variant="contained"
            sx={{
              background: `linear-gradient(135deg, ${brandColors.primary.main}, ${brandColors.secondary.main})`,
              borderRadius: '12px',
              px: 4,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                background: `linear-gradient(135deg, ${brandColors.primary.light}, ${brandColors.secondary.light})`,
              }
            }}
            onClick={() => {
              setUpgradeDialogOpen(false);
              // Handle upgrade logic here - integrate with your payment system
              console.log('Starting upgrade process...');
            }}
          >
            {t('chat.startFreeTrial')}
          </Button>
        </DialogActions>
      </Dialog>
    </ChatContainer>
  );
};

export default Chat;